import { Check, Plus } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import {
  useCreateLocalMonitoringIntentMutation,
  useMonitoringTargetsQuery,
  useTenantSessionQuery
} from "@/api/query-hooks";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const monitoringAppId = "doctor-server-host-agent";

type MonitoringApprovalActionProps = {
  appId: string;
  clientId: number;
  relationKind: "REGISTRATION_REQUEST" | "PRODUCT_ACTIVATION";
  relationId: string;
  os: string;
  hostname: string;
  approveLabel: string;
  disabled?: boolean;
  onApprove: () => Promise<void>;
};

export function MonitoringApprovalAction(props: MonitoringApprovalActionProps) {
  const { t } = useTranslation(["licensing", "common"]);
  const isMonitoring = props.appId === monitoringAppId;
  const session = useTenantSessionQuery(isMonitoring);
  const isLocal = session.data?.targetProvider === "LOCAL";
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<"existing" | "new">("existing");
  const [targetId, setTargetId] = useState("");
  const [targetName, setTargetName] = useState(props.hostname);
  const [intentRequestId] = useState(() => crypto.randomUUID());
  const targets = useMonitoringTargetsQuery(props.clientId, "unbound", isMonitoring && isLocal && open);
  const intent = useCreateLocalMonitoringIntentMutation(props.clientId);
  const activeTargets = useMemo(
    () => targets.data?.pages.flatMap((page) => page.items).filter((target) => target.lifecycle === "active") ?? [],
    [targets.data]
  );

  useEffect(() => {
    if (!targetId && activeTargets[0]) setTargetId(activeTargets[0].id);
    if (activeTargets.length === 0 && targets.isSuccess) setMode("new");
  }, [activeTargets, targetId, targets.isSuccess]);

  const directApproval = !isMonitoring || (session.isSuccess && !isLocal);
  const busy = props.disabled || intent.isPending;

  const beginApproval = () => {
    if (directApproval) {
      void props.onApprove();
      return;
    }
    if (isLocal) setOpen(true);
  };

  const bindAndApprove = async () => {
    const createNew = mode === "new";
    if ((createNew && !targetName.trim()) || (!createNew && !targetId)) return;
    let intentCreated = false;
    try {
      await intent.mutateAsync({
        clientId: props.clientId,
        requestId: intentRequestId,
        relationKind: props.relationKind,
        relationId: props.relationId,
        ...(createNew
          ? {
              createTargetType: props.os.toLowerCase().includes("win") ? "WINDOWS_HOST" : "LINUX_HOST",
              createTargetName: targetName.trim()
            }
          : { targetId })
      });
      intentCreated = true;
      await props.onApprove();
      setOpen(false);
    } catch (error) {
      if (!intentCreated) toast.error(error instanceof Error ? error.message : t("activation.targetApprovalFailed"));
    }
  };

  return (
    <>
      <Button
        size="sm"
        onClick={beginApproval}
        disabled={busy || (isMonitoring && !session.isSuccess)}
        title={isMonitoring && session.isError ? t("activation.authorityUnavailable") : undefined}
      >
        <Check className="h-4 w-4" />{props.approveLabel}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{t("activation.targetDialogTitle")}</DialogTitle>
            <DialogDescription>{t("activation.targetDialogDescription", { host: props.hostname })}</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4">
            <div className="grid gap-2">
              <Label htmlFor={`monitoring-target-mode-${props.relationId}`}>{t("activation.targetChoice")}</Label>
              <Select value={mode} onValueChange={(value) => setMode(value as "existing" | "new")}>
                <SelectTrigger id={`monitoring-target-mode-${props.relationId}`} className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="existing" disabled={activeTargets.length === 0}>{t("activation.useExistingTarget")}</SelectItem>
                  <SelectItem value="new">{t("activation.createNewTarget")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {mode === "existing" ? (
              <div className="grid gap-2">
                <Label htmlFor={`monitoring-target-${props.relationId}`}>{t("activation.existingTarget")}</Label>
                {targets.isLoading ? <p className="text-sm text-muted-foreground">{t("targets.loading")}</p> : null}
                {targets.isError ? (
                  <div className="flex flex-wrap items-center gap-2 text-sm text-destructive">
                    <span>{t("activation.targetLoadFailed")}</span>
                    <Button size="sm" variant="outline" onClick={() => void targets.refetch()}>{t("targets.retry")}</Button>
                  </div>
                ) : null}
                {targets.isSuccess && activeTargets.length > 0 ? (
                  <Select value={targetId} onValueChange={setTargetId}>
                    <SelectTrigger id={`monitoring-target-${props.relationId}`} className="w-full">
                      <SelectValue placeholder={t("activation.selectTarget")} />
                    </SelectTrigger>
                    <SelectContent>
                      {activeTargets.map((target) => <SelectItem key={target.id} value={target.id}>{target.display_name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                ) : null}
                {targets.hasNextPage ? (
                  <Button size="sm" variant="outline" disabled={targets.isFetchingNextPage} onClick={() => void targets.fetchNextPage()}>
                    {t("targets.loadMore")}
                  </Button>
                ) : null}
              </div>
            ) : (
              <div className="grid gap-2">
                <Label htmlFor={`monitoring-target-name-${props.relationId}`}>{t("activation.newTargetName")}</Label>
                <Input
                  id={`monitoring-target-name-${props.relationId}`}
                  value={targetName}
                  maxLength={160}
                  onChange={(event) => setTargetName(event.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  {t("activation.newTargetType", { type: props.os.toLowerCase().includes("win") ? "Windows" : "Linux" })}
                </p>
              </div>
            )}
            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <Button variant="outline" onClick={() => setOpen(false)}>{t("common:actions.cancel")}</Button>
              <Button
                onClick={() => void bindAndApprove()}
                disabled={busy || targets.isError || (mode === "existing" ? !targetId : !targetName.trim())}
              >
                <Plus className="h-4 w-4" />{t("activation.bindAndAllow")}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
